import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const GoogleAnalytics = () => {
  const testimonials = [
    {
      id: 1,
      name: "Saurabh Mishra",
      review:
        "This Google Analytics 4 course is a game-changer! I can now confidently set up event tracking, create custom reports, and interpret data for actionable insights. Best analytics training in Noida!",
      rating: 5,
      date: "2024-05-15",
      profileImg: "https://randomuser.me/api/portraits/men/32.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Nisha Kumari",
      review:
        "Understanding GA4 was challenging, but the instructors made it simple with real-world examples and hands-on exercises. My analytical skills have greatly improved, leading to better campaign optimization.",
      rating: 5,
      date: "2024-05-12",
      profileImg: "https://randomuser.me/api/portraits/women/42.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Rohit Sharma",
      review:
        "From basic navigation to advanced exploration reports and GTM integration, every module was covered in depth. I highly recommend this course for anyone looking to build a career in data analytics.",
      rating: 4,
      date: "2024-05-10",
      profileImg: "https://randomuser.me/api/portraits/men/52.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Pooja Singh",
      review:
        "The practical approach to using Google Analytics and Looker Studio was excellent. I can now create compelling dashboards that help my team make data-driven decisions. The placement support was also very helpful.",
      rating: 5,
      date: "2024-05-08",
      profileImg: "https://randomuser.me/api/portraits/women/62.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Vivek Das",
      review:
        "This course provided me with the necessary skills to transition into a Digital Marketing Analyst role. The focus on GA4's unique data model and event-based tracking was particularly beneficial.",
      rating: 4,
      date: "2024-05-05",
      profileImg: "https://randomuser.me/api/portraits/men/72.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };
  

  const faqContent = [
    {
      title: "What is Google Analytics and why is it crucial for businesses?",
      answer:
        "Google Analytics is a free web analytics service that tracks and reports website traffic. It's crucial because it provides insights into user behavior, website performance, campaign effectiveness, and conversion paths, enabling businesses to make data-driven decisions and optimize their digital strategies.",
    },
    {
      title: "What are the prerequisites for joining this Google Analytics course?",
      answer:
        "There are no strict technical prerequisites. A basic understanding of digital marketing concepts and familiarity with navigating websites is beneficial. This course is designed for aspiring digital marketers, business owners, content creators, and anyone who wants to understand and leverage website data.",
    },
    {
      title: "Will this course cover Google Analytics 4 (GA4)?",
      answer:
        "Yes, this course is **primarily focused on Google Analytics 4 (GA4)**, as Universal Analytics (UA) has been deprecated. You will learn GA4's event-based data model, new reporting interface, explorations, and its integration capabilities. We will also touch upon migration concepts.",
    },
    {
      title: "What specific concepts and tools will I master in this training?",
      answer:
        "You will master GA4 account setup, event and conversion tracking, standard and exploration reports, audience analysis, integration with Google Ads and Search Console, and creating dashboards with Google Looker Studio. You'll also gain proficiency in Google Tag Manager (GTM).",
    },
    {
      title: "Will I get placement assistance after course completion?",
      answer:
        "Yes, we provide 100% placement assistance for our Google Analytics graduates. This includes professional resume building, mock interview preparation, soft skills training, and connecting you with our network of digital marketing agencies and companies for roles like Web Analyst, Digital Marketing Analyst, and Marketing Data Specialist in Noida and across India.",
    },
    {
      title: "What kind of practical experience will I gain?",
      answer:
        "You will get hands-on experience by setting up GA4 on a live website, implementing event tracking via GTM, analyzing real website data, creating custom reports and funnels, and building interactive dashboards in Google Looker Studio. Case studies will help apply concepts to real-world scenarios.",
    },
    {
      title: "What are the career opportunities after this Google Analytics training?",
      answer:
        "Upon successful completion, you can pursue roles such as Web Analyst, Digital Marketing Analyst, Marketing Data Specialist, Business Intelligence Analyst (entry-level), Performance Marketing Analyst, or work as a freelance Analytics Consultant.",
    },
    {
      title: "What is the duration and mode of training?",
      answer:
        "The course duration varies based on the pace (e.g., intensive fast-track vs. regular batch). We offer both immersive offline classroom training at our Noida center and interactive live online classes, providing flexibility to suit your learning preference.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Web Analytics & GA4",
      description: "Understanding the importance of data, the evolution of Google Analytics, key differences between UA and GA4, and the event-based data model.",
    },
    {
      title: "Module 2: Google Analytics 4 Account Setup & Configuration",
      description: "Setting up a GA4 property, data streams (web, app), linking Google Ads, Search Console, and user permissions.",
    },
    {
      title: "Module 3: Event Tracking & Conversions in GA4",
      description: "Understanding automatic, enhanced, and custom events. Defining conversions, creating custom events, and using the debug view.",
    },
    {
      title: "Module 4: Google Tag Manager (GTM) for GA4",
      description: "Introduction to GTM, setting up containers, tags, triggers, and variables for efficient event and conversion tracking without code changes.",
    },
    {
      title: "Module 5: Standard GA4 Reports & Navigation",
      description: "Understanding the GA4 interface, real-time reports, acquisition, engagement, monetization, and retention reports.",
    },
    {
      title: "Module 6: GA4 Explorations (Custom Reporting)",
      description: "Creating custom reports using Explorations: Free-form, Funnel exploration, Path exploration, Segment overlap, and User explorer.",
    },
    {
      title: "Module 7: Audience & User Behavior Analysis",
      description: "Analyzing user demographics, tech details, user lifecycle, segments, and understanding user journeys on your website.",
    },
    {
      title: "Module 8: E-commerce Tracking & Reporting",
      description: "Setting up and analyzing e-commerce events like view_item, add_to_cart, purchase, and understanding e-commerce funnels in GA4.",
    },
    {
      title: "Module 9: Google Looker Studio (Data Studio) for GA4",
      description: "Connecting GA4 data to Looker Studio, creating interactive dashboards, charts, and customizing reports for stakeholders.",
    },
    {
      title: "Module 10: Attribution Models & Data-Driven Decisions",
      description: "Understanding various attribution models (last click, first click, data-driven) and using GA4 data to optimize marketing spend.",
    },
    {
      title: "Module 11: Debugging, Troubleshooting & Best Practices",
      description: "Identifying common GA4 implementation issues, using GTM debug mode, and applying best practices for accurate data collection.",
    },
    {
      title: "Module 12: Live Project & Case Studies",
      description: "Apply all learned GA4 concepts to a real website or client project, from setup and tracking to advanced reporting and insights generation. Build your analytics portfolio.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Google Analytics 4 (GA4) Expert Course in Noida | Master Web Data</title>
        <meta
          name="description"
          content="Become a certified Google Analytics 4 (GA4) Expert in Noida. Our comprehensive training covers GA4 implementation, event tracking, reporting, Looker Studio, and GTM with placement support."
        />
        <meta
          name="keywords"
          content="Google Analytics course Noida, GA4 training, web analytics, data analysis, Google Tag Manager, Looker Studio, digital marketing analytics, conversion tracking, GA4 certification, best GA4 course"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://www.hostgator.com/blog/wp-content/uploads/2020/09/Google-Analytics-Beginner_s-Guide.jpg"
          alt="Google Analytics Expert Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Become a <span className="text-blue-400">Google Analytics 4</span> Expert in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Master web data, user behavior, and reporting with GA4 to drive smarter marketing and business decisions.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-blue-700 hover:bg-blue-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
               <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Google Analytics Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn Google Analytics Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Master Google Analytics 4?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            In today's digital world, data is king. Mastering Google Analytics 4 (GA4) empowers you to understand user behavior, measure marketing effectiveness, identify growth opportunities, and make data-driven decisions that propel businesses forward.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-cyan-700 mb-3">Data-Driven Decisions</h3>
              <p className="text-gray-600">Transform raw data into actionable insights for business growth.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-cyan-700 mb-3">High Demand Skill</h3>
              <p className="text-gray-600">Analytics professionals are crucial for optimizing digital strategies.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-cyan-700 mb-3">Boost Marketing ROI</h3>
              <p className="text-gray-600">Measure campaign performance and allocate budgets effectively.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '📊', title: 'Comprehensive GA4 Mastery', desc: 'Deep dive into GA4 features, reports, and its event-based model.' },
              { icon: '⚙️', title: 'Google Tag Manager Expertise', desc: 'Implement tracking efficiently without developer intervention.' },
              { icon: '📈', title: 'Actionable Reporting & Dashboards', desc: 'Create insightful reports and powerful dashboards using Looker Studio.' },
              { icon: '👨‍🏫', title: 'Industry Expert Trainers', desc: 'Learn from certified analytics professionals with real-world experience.' },
              { icon: '🏆', title: 'Certification & Portfolio Building', desc: 'Earn a recognized certificate and build a portfolio of analytics projects.' },
              { icon: '🤝', title: '100% Placement Assistance', desc: 'Dedicated support for job search, resume preparation, and interviews.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-blue-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-blue-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Platforms You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our Google Analytics Expert training ensures you gain hands-on proficiency with the most critical web analytics and reporting tools.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'Google Analytics 4 (GA4)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Primary analytics platform' },
              { name: 'Google Tag Manager (GTM)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Tag deployment & management' },
              { name: 'Google Looker Studio', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Data visualization & dashboards' },
              { name: 'Google Search Console', logo: 'https://wonkrew.com/wp-content/uploads/2022/10/Google-Search-Console-logo-Wonkrew-1024x614.png', desc: 'Search performance insights' },
              
              
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2 object-contain" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
                {tool.desc && <p className="text-gray-500 text-xs mt-1">{tool.desc}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities in Web Analytics</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A certification as a **Google Analytics 4 Expert** positions you for high-demand roles where you'll drive impactful business decisions through data.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '📈', title: 'Web Analyst', desc: 'Analyze website performance and user behavior to optimize online presence.' },
              { icon: '📊', title: 'Digital Marketing Analyst', desc: 'Measure the effectiveness of marketing campaigns and identify areas for improvement.' },
              { icon: '💡', title: 'Data Marketing Specialist', desc: 'Focus on leveraging data to inform and execute marketing strategies.' },
              { icon: '💻', title: 'Business Intelligence Analyst (Entry-level)', desc: 'Translate data into actionable business insights for various departments.' },
              { icon: '🔗', title: 'CRO (Conversion Rate Optimization) Specialist', desc: 'Use analytics to identify friction points and improve conversion funnels.' },
              { icon: '🔍', title: 'Freelance Analytics Consultant', desc: 'Provide data analysis and reporting services to multiple clients.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-blue-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our Google Analytics 4 Expert training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Google Analytics Expert Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name (Optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-cyan-900">
            What Our Google Analytics Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-cyan-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-cyan-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-cyan-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default GoogleAnalytics;